import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { Pago } from 'data/pagos/pago';

import PagosGeneralesTopSection from 'components/modules/project-management/PagosGeneralesTopSection';
import PagosListTable, { pagosListTableColumns } from 'components/tables/PagosListTable';
import { defaultBreadcrumbItems } from 'data/commonData';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { useAuth } from 'providers/AuthProvider';
import { Form } from 'react-bootstrap';
import { co } from '@fullcalendar/core/internal-common';

interface Gasto {
  gastoId: string;
  gasto: string;
}

interface Miembro {
  Iniciales: string;
}

const PagosGeneralesListView = () => {
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [pagosPendientes, setPagosPendientes] = useState<number>(0);
  const [pagosPagados, setPagosPagados] = useState<number>(0);
  const [categorias, setCategorias] = useState<Gasto[]>([]);
  const [pagadores, setPagadores] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'Todos los pagadores' }
  ]);
  const { user, loading, logout } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';
  
  // State for filters
  const today = new Date();
  const [month, setMonth] = useState<string>(String(today.getMonth() + 1).padStart(2, '0'));
  const [year, setYear] = useState<string>(String(today.getFullYear()));
  const [category, setCategory] = useState<string>('');
  const [estado, setEstado] = useState<string>('');
  const [caja, setCaja] = useState<string>('');

  // Fetch categorias (Gastos) for the dropdown
  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const response = await axios.get<Gasto[]>(`${apiUrl}/gastos/`);
        setCategorias(response.data);
      } catch (error) {
        console.error('Error fetching gastos: ', error);
      }
    };
    fetchCategorias();
  }, []);


  // Fetch pagos with filters
  useEffect(() => {
    const fetchPagos = async () => {
      try {
        const params: Record<string, string> = {};
        if (month) params.month = month;
        if (year) params.year = year;
        if (category) params.gastoIdFkEnPagos = category;
        if (estado) params.estado = estado;
        params.caja = 'cash';

        const response = await axios.get<Pago[]>(`${apiUrl}/pagos/pendientes`, { params });
        const data = response.data;
        console.log('Fetched pagos: ', data);
        setPagos(data);

        // Calculate total pending payments
        const totalPendientes = data
        .filter((pago: Pago) => pago.estado === 'Pendiente')
        .reduce((sum: number, pago: Pago) => sum + (pago.importe || 0), 0);
          setPagosPendientes(totalPendientes);


      } catch (error) {
        console.error('ha habido un error: ', error);
      }
    };
    fetchPagos();
  }, [month, year, category, estado, caja]);

  const months = [
    { value: '', label: 'Todos los meses' },
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  const years = [
    { value: '', label: 'Todos los años' },
    { value: '2025', label: '2025' },
    { value: '2024', label: '2024' },
    { value: '2023', label: '2023' }
  ];

  const estados = [
    { value: '', label: 'Todos los estados' },
    { value: 'Pendiente', label: 'Pendiente' },
    { value: 'Pagado', label: 'Pagado' },
    { value: 'Cancelado', label: 'Cancelado' },
    { value: 'CAP entrega a FAM', label: 'CAP entrega a FAM' }
  ];

  const table = useAdvanceTable({
    data: pagos,
    columns: pagosListTableColumns,
    pageSize: 2400,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center justify-content-between">
          <h2 className="mb-0">
            <span className="me-3">Pagos Pendientes</span>
            <span className="fw-normal text-body-tertiary"></span>
          </h2>
          <div className="d-flex align-items-center gap-3">
            <h4 className="mb-0 text-end">
              Pendientes: ${pagosPendientes.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </h4>
            <Link className="btn btn-primary px-5" to="/app/apps/pagos/createpago">
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Agregar Nuevo
            </Link>
          </div>
        </div>
        <div className="mb-4">
          <Form>
            <div className="d-flex flex-wrap gap-3">
              <Form.Group>
                <Form.Label>Mes</Form.Label>
                <Form.Select value={month} onChange={(e) => setMonth(e.target.value)}>
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Año</Form.Label>
                <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y) => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Categoría</Form.Label>
                <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">Todas las categorías</option>
                  {categorias.map((c) => (
                    <option key={c.gastoId} value={c.gastoId}>{c.gasto}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select value={estado} onChange={(e) => setEstado(e.target.value)}>
                  {estados.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </Form>
        </div>
        <PagosGeneralesTopSection activeView="list" />
        <PagosListTable />
      </AdvanceTableProvider>
    </div>
  );
};

export default PagosGeneralesListView;